import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import Axios from "../../services/AxiosInstance";

const DEFAULT_PER_PAGE = Number(process.env.REACT_APP_PER_PAGE) || 10;

// HR LIST shows Partner Portal HR users + their server-provided employee
// counts. HR filtering and counting are enforced by the backend
// (GET api/admin/hr-list); this page only displays `employeeCount` and
// never filters by role or counts employees on the client.
const HrList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [viewer, setViewer] = useState(null);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortKey, setSortKey] = useState("full_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [editHr, setEditHr] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: "",
    email: "",
    mobile_no: "",
    status: "Active",
  });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [statusPending, setStatusPending] = useState(null);

  const [toast, setToast] = useState("");
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const fetchHrList = useCallback(
    async (pageNo) => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          pageNo: String(pageNo),
          perPage: String(perPage),
          sortkey: sortKey,
          order: sortOrder,
        });
        if (appliedSearch) params.set("search", appliedSearch);
        if (statusFilter) params.set("status", statusFilter);

        const res = await Axios.get(`api/admin/hr-list?${params.toString()}`);
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        const pagination = res.data?.pagination || {};
        // Current Admin card data (never part of the HR table below).
        setViewer(res.data?.viewer && res.data.viewer.id ? res.data.viewer : null);
        setItems(list);
        setTotal(Number(pagination.total) || 0);
        setTotalPages(Number(pagination.totalPages) || 0);
        setPage(Number(pagination.page) || pageNo);
      } catch (err) {
        const httpStatus = err?.response?.status;
        setError(
          httpStatus === 401 || httpStatus === 403
            ? "You are not authorized to view the HR list. Please log in again."
            : "Failed to load HR users. Please retry."
        );
        setItems([]);
        setTotal(0);
        setTotalPages(0);
        setViewer(null);
      } finally {
        setLoading(false);
      }
    },
    [perPage, appliedSearch, statusFilter, sortKey, sortOrder]
  );

  useEffect(() => {
    fetchHrList(page);
  }, [fetchHrList, page]);

  const applyFilters = () => {
    setAppliedSearch(search.trim());
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setStatusFilter("");
    setSortKey("full_name");
    setSortOrder("asc");
    setPage(1);
  };

  // Server-side sorting (same sortkey/order contract as BusinessList etc.).
  // Changing sort resets to page 1; active search/filter values are kept.
  const handleSort = (key, order) => {
    setSortKey(key);
    setSortOrder(order);
    setPage(1);
  };

  // Sort arrows follow the existing Admin table pattern
  // (fa-arrow-up / fa-arrow-down in <th>); the active direction is tinted.
  const sortArrows = (key) => (
    <>
      <i
        onClick={() => handleSort(key, "asc")}
        className="fa fa-arrow-up"
        style={{
          fontSize: "10px",
          cursor: "pointer",
          marginLeft: "4px",
          color: sortKey === key && sortOrder === "asc" ? "#ffc107" : undefined,
        }}
      ></i>
      <i
        onClick={() => handleSort(key, "desc")}
        className="fa fa-arrow-down"
        style={{
          fontSize: "10px",
          cursor: "pointer",
          marginLeft: "2px",
          color: sortKey === key && sortOrder === "desc" ? "#ffc107" : undefined,
        }}
      ></i>
    </>
  );

  const handlePageClick = (event) => {
    setPage(event.selected + 1);
  };

  const openEdit = (hr) => {
    setEditHr(hr);
    setEditForm({
      full_name: hr.name || "",
      email: hr.email || "",
      mobile_no: hr.phone || "",
      status: hr.status || "Active",
    });
    setModalError("");
  };

  const saveEdit = async () => {
    if (!editHr || saving) return;
    setSaving(true);
    setModalError("");
    try {
      await Axios.put(
        `api/admin/hr-list/${encodeURIComponent(editHr.id)}`,
        {
          full_name: editForm.full_name,
          email: editForm.email,
          mobile_no: editForm.mobile_no,
          status: editForm.status,
        }
      );
      setEditHr(null);
      showToast("HR updated successfully");
      fetchHrList(page);
    } catch (err) {
      setModalError("Failed to update HR. Please check the values and retry.");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (hr, nextStatus) => {
    const activating = nextStatus === "Active";
    const confirmed = window.confirm(
      activating
        ? `Activate ${hr.name || hr.id}? The HR account will be Active again.`
        : `Deactivate ${hr.name || hr.id}? This will deactivate the HR account (soft deactivation). ` +
          `Their ${hr.employeeCount ?? 0} existing employee(s) will NOT be deleted. ` +
          `The account can be activated again later. ` +
          `If this account also has Admin access, deactivation may block its login.`
    );
    if (!confirmed || statusPending) return;
    setStatusPending(hr.id);
    try {
      await Axios.put(
        `api/admin/hr-list/${encodeURIComponent(hr.id)}/status`,
        { status: nextStatus }
      );
      showToast(
        activating ? "HR activated successfully" : "HR deactivated successfully"
      );
      fetchHrList(page);
    } catch (err) {
      const httpStatus = err?.response?.status;
      setError(
        httpStatus === 401 || httpStatus === 403
          ? "You are not authorized to change the HR status. Please log in again."
          : `Failed to ${activating ? "activate" : "deactivate"} HR. Please retry.`
      );
    } finally {
      setStatusPending(null);
    }
  };

  const statusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "active") return "bg-success";
    if (s === "inactive") return "bg-warning text-dark";
    return "bg-secondary";
  };

  return (
    <div className="container mt-4" style={{ maxWidth: "1280px", margin: "0 auto" }}>
      <h1
        className="text-center mb-4"
        style={{ fontWeight: "700", color: "#0B2B4C" }}
      >
        HR LIST
      </h1>
      <p className="text-center text-muted mb-4">
        All Partner Portal HR users with the number of employees created by
        each HR.
      </p>

      {viewer && (
        <div
          className="card mb-4"
          style={{
            border: "none",
            borderRadius: "15px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "#0B2B4C",
              color: "#fff",
              padding: "15px 25px",
            }}
          >
            <span style={{ fontSize: "20px", fontWeight: "600" }}>
              Current Admin
            </span>
          </div>
          <div className="p-4">
            <div className="row g-3">
              <div className="col-md-3">
                <div className="text-muted" style={{ fontSize: "12px" }}>Admin Name</div>
                <div style={{ fontWeight: 600 }}>{viewer.name || "-"}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted" style={{ fontSize: "12px" }}>Email</div>
                <div style={{ fontWeight: 600 }}>{viewer.email || "-"}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted" style={{ fontSize: "12px" }}>Mobile</div>
                <div style={{ fontWeight: 600 }}>{viewer.mobile || "-"}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted" style={{ fontSize: "12px" }}>Status</div>
                <div>
                  {viewer.status ? (
                    <span className={`badge ${statusBadge(viewer.status)}`}>
                      {viewer.status}
                    </span>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="card mb-4"
        style={{
          border: "none",
          borderRadius: "15px",
          boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "#0B2B4C",
            color: "#fff",
            padding: "15px 25px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "20px", fontWeight: "600" }}>
            Filter HR Users
          </span>
          <span style={{ fontSize: "14px", opacity: 0.85 }}>
            {total} record{total === 1 ? "" : "s"}
          </span>
        </div>

        <div className="p-4">
          <div className="row g-3 hr-list-filters">
            <div className="col-md-5">
              <input
                type="text"
                className="form-control"
                placeholder="Search HR name, email or id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyFilters();
                }}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-4 d-flex gap-2">
              <button className="btn btn-primary flex-fill" onClick={applyFilters}>
                Search
              </button>
              <button
                className="btn btn-secondary flex-fill"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
            <div className="col-md-4 d-flex align-items-center gap-2">
              <label className="mb-0" htmlFor="hr-list-per-page">
                Per page:
              </label>
              <select
                id="hr-list-per-page"
                className="form-control"
                style={{ maxWidth: "110px" }}
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value) || DEFAULT_PER_PAGE);
                  setPage(1);
                }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-outline-primary"
                onClick={() => fetchHrList(page)}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2 text-muted">Loading HR users...</div>
        </div>
      ) : error ? (
        <div className="alert alert-danger text-center">
          {error}{" "}
          <button
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={() => fetchHrList(page)}
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="alert alert-info text-center">
          No HR users found. Try adjusting filters or check back after HR users
          are added in the Partner Portal.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead className="table-dark">
              <tr>
                <th>S.No</th>
                <th style={{ minWidth: "160px" }}>HR Name{sortArrows("full_name")}</th>
                <th style={{ minWidth: "200px" }}>Email</th>
                <th style={{ minWidth: "120px" }}>Phone</th>
                <th>Business</th>
                <th>Status{sortArrows("status")}</th>
                <th style={{ minWidth: "240px" }}>Actions</th>
                <th>Employees{sortArrows("employeeCount")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((hr, index) => (
                <tr key={hr.id || index}>
                  <td>{(page - 1) * perPage + index + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {hr.id ? (
                        <Link to={`/hr-list/${encodeURIComponent(hr.id)}`}>
                          {hr.name || "-"}
                        </Link>
                      ) : (
                        hr.name || "-"
                      )}
                    </div>
                    {hr.id && (
                      <div className="text-muted" style={{ fontSize: "12px" }}>
                        {hr.id}
                      </div>
                    )}
                  </td>
                  <td>{hr.email || "-"}</td>
                  <td>{hr.phone || "-"}</td>
                  <td>{hr.business_name || "-"}</td>
                  <td>
                    {hr.status ? (
                      <span className={`badge ${statusBadge(hr.status)}`}>
                        {hr.status}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={{ minWidth: "240px" }}>
                    <div className="hr-list-actions">
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => openEdit(hr)}
                      >
                        <span aria-hidden="true">✏</span> Update
                      </button>
                      {String(hr.status || "").toLowerCase() === "inactive" ? (
                        <button
                          className="btn btn-success btn-sm"
                          disabled={statusPending === hr.id}
                          onClick={() => toggleStatus(hr, "Active")}
                        >
                          <span aria-hidden="true">▶</span> Activate
                        </button>
                      ) : (
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={statusPending === hr.id}
                          onClick={() => toggleStatus(hr, "Inactive")}
                        >
                          <span aria-hidden="true">⏸</span> Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="badge bg-primary">
                      {hr.employeeCount ?? 0}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="d-flex flex-column align-items-center mt-3">
          <ReactPaginate
            previousLabel="Previous"
            nextLabel="Next"
            breakLabel="..."
            pageCount={totalPages}
            forcePage={Math.min(page, totalPages) - 1}
            onPageChange={handlePageClick}
            containerClassName="pagination justify-content-center"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            activeClassName="active"
            disabledClassName="disabled"
          />
          <div className="text-muted" style={{ fontSize: "14px" }}>
            Page {page} of {totalPages} ({total} records)
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: "fixed",
          bottom: "65px",
          right: "20px",
          background: "#28a745",
          color: "white",
          padding: "12px 20px",
          borderRadius: "6px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          zIndex: 2000
        }}>
          {toast}
        </div>
      )}

      {editHr && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000
        }}>
          <div className="hr-list-filters" style={{
            background: "white",
            padding: "25px",
            borderRadius: "8px",
            width: "440px",
            maxWidth: "92%"
          }}>
            <h4>Edit HR</h4>

            {modalError && (
              <div className="alert alert-danger">{modalError}</div>
            )}

            <label className="form-label mt-2 mb-0" htmlFor="hr-edit-name">
              Full Name
            </label>
            <input
              id="hr-edit-name"
              className="form-control mb-2"
              value={editForm.full_name}
              onChange={(e) =>
                setEditForm({ ...editForm, full_name: e.target.value })
              }
            />

            <label className="form-label mt-1 mb-0" htmlFor="hr-edit-email">
              Email
            </label>
            <input
              id="hr-edit-email"
              className="form-control mb-2"
              value={editForm.email}
              onChange={(e) =>
                setEditForm({ ...editForm, email: e.target.value })
              }
            />

            <label className="form-label mt-1 mb-0" htmlFor="hr-edit-mobile">
              Mobile Number
            </label>
            <input
              id="hr-edit-mobile"
              className="form-control mb-2"
              value={editForm.mobile_no}
              onChange={(e) =>
                setEditForm({ ...editForm, mobile_no: e.target.value })
              }
            />

            <label className="form-label mt-1 mb-0" htmlFor="hr-edit-status">
              Status
            </label>
            <select
              id="hr-edit-status"
              className="form-control mb-3"
              value={editForm.status}
              onChange={(e) =>
                setEditForm({ ...editForm, status: e.target.value })
              }
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            <div className="d-flex">
              <button
                className="btn btn-success"
                disabled={saving}
                onClick={saveEdit}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                className="btn btn-danger ms-2"
                disabled={saving}
                onClick={() => setEditHr(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HrList;
